import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format, subDays } from 'date-fns';

function Productivity({ user }) {
  const [driversKPIs, setDriversKPIs] = useState([]);
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('score'); // score, revenue, deliveries, attendance

  useEffect(() => {
    fetchAllKPIs();
  }, [startDate, endDate]);

  const fetchAllKPIs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Récupérer tous les chauffeurs
      const driversResponse = await axios.get('/api/drivers', {
        headers: { Authorization: `Bearer ${token}` }
      });

      // KPIs de chaque chauffeur
      const kpisPromises = driversResponse.data.map(async (driver) => {
        try {
          const kpis = await axios.get(`/api/kpis/driver/${driver.id}`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { start_date: startDate, end_date: endDate }
          });
          return {
            ...kpis.data,
            id: driver.id,
            name: `${driver.firstname} ${driver.lastname}`,
            vehicle: driver.vehicle_plate,
            email: driver.email
          };
        } catch (err) {
          return null;
        }
      });

      const kpis = await Promise.all(kpisPromises);
      setDriversKPIs(kpis.filter(k => k !== null));
    } catch (error) {
      console.error('Erreur KPIs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return '#27ae60';
    if (score >= 80) return '#2ecc71';
    if (score >= 70) return '#f39c12';
    if (score >= 60) return '#e67e22';
    return '#e74c3c';
  };

  const getScoreBadge = (score) => {
    if (score >= 90) return 'Excellence';
    if (score >= 80) return 'Très bon';
    if (score >= 70) return 'Bon';
    if (score >= 60) return 'Moyen';
    return 'À améliorer';
  };

  const sortedDrivers = [...driversKPIs].sort((a, b) => {
    switch (sortBy) {
      case 'score':
        return (b.globalScore?.total || 0) - (a.globalScore?.total || 0);
      case 'revenue':
        return (b.productivity?.total_revenue || 0) - (a.productivity?.total_revenue || 0);
      case 'deliveries':
        return (b.productivity?.total_deliveries || 0) - (a.productivity?.total_deliveries || 0);
      case 'attendance':
        return (b.attendance?.rate || 0) - (a.attendance?.rate || 0);
      case 'name':
        return a.name.localeCompare(b.name);
      default:
        return 0;
    }
  });

  if (loading) {
    return (
      <div className="container">
        <div className="loading">Chargement des KPIs...</div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <h2 style={{ margin: 0 }}>Productivité & Performance</h2>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <label style={{ fontSize: '0.85rem', marginRight: '0.5rem' }}>Du</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.85rem', marginRight: '0.5rem' }}>Au</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
              />
            </div>
          </div>
        </div>

        {/* Stats globales */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          <div style={{
            padding: '1.5rem',
            background: 'linear-gradient(135deg, #B8472E 0%, #6E2A19 100%)',
            color: 'white',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              {driversKPIs.length}
            </div>
            <div style={{ fontSize: '0.9rem' }}>Chauffeurs actifs</div>
          </div>

          <div style={{
            padding: '1.5rem',
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              {driversKPIs.reduce((sum, d) => sum + (d.productivity?.total_revenue || 0), 0).toFixed(0)}€
            </div>
            <div style={{ fontSize: '0.9rem' }}>CA Total</div>
          </div>

          <div style={{
            padding: '1.5rem',
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              {driversKPIs.reduce((sum, d) => sum + (d.productivity?.total_deliveries || 0), 0)}
            </div>
            <div style={{ fontSize: '0.9rem' }}>Livraisons totales</div>
          </div>

          <div style={{
            padding: '1.5rem',
            background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            color: 'white',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              {(driversKPIs.reduce((sum, d) => sum + (d.globalScore?.total || 0), 0) / driversKPIs.length).toFixed(1)}
            </div>
            <div style={{ fontSize: '0.9rem' }}>Score Moyen</div>
          </div>
        </div>

        {/* Filtres de tri */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '2rem',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontWeight: 'bold', marginRight: '0.5rem', lineHeight: '2.5rem' }}>Trier par:</span>
          {[
            { value: 'score', label: 'Score' },
            { value: 'revenue', label: 'CA' },
            { value: 'deliveries', label: 'Livraisons' },
            { value: 'attendance', label: 'Assiduité' },
            { value: 'name', label: 'Nom' }
          ].map(sort => (
            <button
              key={sort.value}
              onClick={() => setSortBy(sort.value)}
              style={{
                padding: '0.5rem 1rem',
                background: sortBy === sort.value ? '#B8472E' : '#f8f9fa',
                color: sortBy === sort.value ? 'white' : '#333',
                border: '1px solid #ddd',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: sortBy === sort.value ? 'bold' : 'normal'
              }}
            >
              {sort.label}
            </button>
          ))}
        </div>

        {/* Tableau des performances */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ padding: '1rem', textAlign: 'left', color: '#333' }}>#</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: '#333' }}>Chauffeur</th>
                <th style={{ padding: '1rem', textAlign: 'center', color: '#333' }}>Score Global</th>
                <th style={{ padding: '1rem', textAlign: 'center', color: '#333' }}>CA</th>
                <th style={{ padding: '1rem', textAlign: 'center', color: '#333' }}>Livraisons</th>
                <th style={{ padding: '1rem', textAlign: 'center', color: '#333' }}>Jours</th>
                <th style={{ padding: '1rem', textAlign: 'center', color: '#333' }}>Assiduité</th>
                <th style={{ padding: '1rem', textAlign: 'center', color: '#333' }}>Réclamations</th>
                <th style={{ padding: '1rem', textAlign: 'center', color: '#333' }}>Retards</th>
                <th style={{ padding: '1rem', textAlign: 'center', color: '#333' }}>Accidents</th>
                <th style={{ padding: '1rem', textAlign: 'center', color: '#333' }}>PV</th>
                <th style={{ padding: '1rem', textAlign: 'center', color: '#333' }}>Team</th>
              </tr>
            </thead>
            <tbody>
              {sortedDrivers.map((driver, index) => {
                const score = driver.globalScore?.total || 0;
                const scoreColor = getScoreColor(score);

                return (
                  <tr
                    key={driver.id}
                    style={{
                      borderBottom: '1px solid #e0e0e0',
                      background: index % 2 === 0 ? 'white' : '#f8f9fa'
                    }}
                  >
                    <td style={{ padding: '1rem', fontWeight: 'bold', fontSize: '1.2rem' }}>
                      {index + 1}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ fontWeight: 'bold' }}>{driver.name}</div>
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>
                        {driver.vehicle || 'N/A'}
                      </div>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <div style={{
                        display: 'inline-block',
                        padding: '0.5rem 1rem',
                        background: scoreColor,
                        color: 'white',
                        borderRadius: '20px',
                        fontWeight: 'bold',
                        fontSize: '1.1rem'
                      }}>
                        {score.toFixed(1)}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.3rem' }}>
                        {getScoreBadge(score)}
                      </div>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold' }}>
                      {(driver.productivity?.total_revenue || 0).toFixed(0)}€
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold' }}>
                      {driver.productivity?.total_deliveries || 0}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      {driver.productivity?.working_days || 0}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <span style={{
                        color: (driver.attendance?.rate || 0) >= 90 ? '#27ae60' : '#e67e22'
                      }}>
                        {(driver.attendance?.rate || 0).toFixed(0)}%
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <span style={{
                        color: (driver.quality?.complaints || 0) === 0 ? '#27ae60' : '#e74c3c'
                      }}>
                        {driver.quality?.complaints || 0}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <span style={{
                        color: (driver.attendance?.delays || 0) === 0 ? '#27ae60' : '#e67e22'
                      }}>
                        {driver.attendance?.delays || 0}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <span style={{
                        color: (driver.safety?.accidents || 0) === 0 ? '#27ae60' : '#e74c3c'
                      }}>
                        {driver.safety?.accidents || 0}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <div style={{
                        color: (driver.safety?.penalties || 0) === 0 ? '#27ae60' : '#e74c3c',
                        fontWeight: 'bold'
                      }}>
                        {driver.safety?.penalties || 0}
                      </div>
                      {(driver.safety?.penalty_amount || 0) > 0 && (
                        <div style={{ fontSize: '0.75rem', color: '#e67e22' }}>
                          {(driver.safety?.penalty_amount || 0).toFixed(0)}€
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <span style={{
                        color: (driver.teamSpirit?.overall_score || 0) >= 4 ? '#27ae60' : '#e67e22'
                      }}>
                        {(driver.teamSpirit?.overall_score || 0).toFixed(1)}/5
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Légende */}
        <div style={{
          marginTop: '2rem',
          padding: '1.5rem',
          background: '#f8f9fa',
          borderRadius: '12px'
        }}>
          <h4 style={{ marginBottom: '1rem' }}>Légende des indicateurs:</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            <div>
              <strong>CA:</strong> Chiffre d'affaires total généré
            </div>
            <div>
              <strong>Livraisons:</strong> Nombre total de livraisons effectuées
            </div>
            <div>
              <strong>Jours:</strong> Nombre de jours travaillés
            </div>
            <div>
              <strong>Assiduité:</strong> Taux de présence (≥90% = bon)
            </div>
            <div>
              <strong>Réclamations:</strong> Nombre de réclamations clients
            </div>
            <div>
              <strong>Retards:</strong> Nombre de retards constatés
            </div>
            <div>
              <strong>Accidents:</strong> Nombre d'accidents déclarés
            </div>
            <div>
              <strong>PV:</strong> Nombre de PV/contraventions
            </div>
            <div>
              <strong>Team:</strong> Note moyenne des collègues (/5)
            </div>
          </div>
        </div>

        {/* Top 3 */}
        <div style={{ marginTop: '2rem' }}>
          <h3 style={{ marginBottom: '1.5rem' }}>Top 3 Performers</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {sortedDrivers.slice(0, 3).map((driver, index) => {
              const score = driver.globalScore?.total || 0;
              const gradients = [
                'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
              ];

              return (
                <div
                  key={driver.id}
                  style={{
                    padding: '1.5rem',
                    background: gradients[index],
                    color: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}
                >
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem', fontWeight: 'bold' }}>#{index + 1}</div>
                  <h4 style={{ margin: '0 0 0.5rem 0' }}>{driver.name}</h4>
                  <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '1rem' }}>
                    {driver.vehicle || 'N/A'}
                  </div>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    {score.toFixed(1)}/100
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '0.5rem',
                    fontSize: '0.85rem'
                  }}>
                    <div>CA: {(driver.productivity?.total_revenue || 0).toFixed(0)}€</div>
                    <div>Livr: {driver.productivity?.total_deliveries || 0}</div>
                    <div>Assid: {(driver.attendance?.rate || 0).toFixed(0)}%</div>
                    <div>Team: {(driver.teamSpirit?.overall_score || 0).toFixed(1)}/5</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Productivity;
